import React, { useState } from "react";
import { Link } from "react-router-dom";
import { authAPI } from "../authAPI";
import { jwtDecode } from "jwt-decode";
import { WEBCHAT_CONFIG, CHAT_AUTH_TOKEN } from "./webchatConfig";
import AuthLayout, {
  AuthAlert,
  AuthOtpModal,
  AuthSecurityNote,
  EyeIcon,
  authButtonClass,
  authEyeButtonClass,
  authInputClass,
  authLabelClass,
  authLinkClass,
} from "./AuthLayout";
import ProductAuthLayout from "./ProductAuthLayout";

// `product` (e.g. "market-researcher") switches to that product's branded screen; login logic is shared
const Login = ({ onLogin, product }) => {
  const Layout = product ? ProductAuthLayout : AuthLayout;
  const signupPath = product ? `/${product}/signup` : "/signup";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (email.trim() && password.trim()) {
      setIsLoading(true);
      setError("");

      const result = await authAPI.loginWithPassword(
        email.trim(),
        password.trim()
      );

      if (result.success) {
        const userData = {
          id: result.data.customer?.companyCustomerId || result.data.customerId || Date.now(),
          name: result.data.customer?.companyCustomerName || result.data.companyCustomerName || "User",
          email: email.trim(),
          phone: result.data.customer?.companyCustomerPriPhone || result.data.companyCustomerPriPhone || "",
          avatar:
            (result.data.customer?.companyCustomerName || result.data.companyCustomerName || "U").charAt(0).toUpperCase(),
          isPro: false,
          ...result.data,
          ...(result.data.customer || {}), // Flatten customer data to top level
          token: result.data.token, // Ensure top-level token is preserved
          userId: (result.data.token ? jwtDecode(result.data.token).userId : null) // Explicitly expose userId
        };

        // Set external_id for chat persistence
        if (result.data.customerId) {
          localStorage.setItem("external_id", result.data.customerId.toString());
        }

        // Decode token and persist chat session details
        try {
          if (result.data.token) {
            const decoded = jwtDecode(result.data.token);
            const userRole = decoded.role;
            const userId = decoded.userId;
            const userToken = decoded.userToken || result.data.token; // Fallback to main token if not in payload

            // Update user session in localStorage for Usewebchat.js to pick up
            const chatSession = {
              userId: userId,
              userToken: userToken,
              conversationId: "", // Will be fetched later
              webchatId: userId,
              lastMessageId: null
            };

            // Store cleanly for chat initialization
            localStorage.setItem(WEBCHAT_CONFIG.clientName, JSON.stringify(chatSession));
            console.log("✅ Chat session seeded from Login:", chatSession);

            // Assign User Role logic
            // Now we have the userId directly from token, no need to rely on potentially empty stored session
            if (userId && userRole && WEBCHAT_CONFIG.uuid) {
              console.log("Assigning role:", userRole, "to userId:", userId);

              // Don't await this to avoid blocking login flow if it fails
              authAPI.addUserRole(WEBCHAT_CONFIG.uuid, userId, userRole, CHAT_AUTH_TOKEN)
                .catch(err => console.error("Error assigning user role:", err));
            }
          }
        } catch (err) {
          console.error("Error processing login token:", err);
        }

        onLogin(userData);
      } else {
        setError(result.error);
      }

      setIsLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (email.trim()) {
      setIsLoading(true);
      setError("");

      const result = await authAPI.sendLoginOTP(email.trim());

      if (result.success) {
        setShowOtpModal(true);
        setError("");
      } else {
        setError(result.error);
      }

      setIsLoading(false);
    } else {
      setError("Please enter your email address first");
    }
  };

  const handleOTPLogin = async (e) => {
    e.preventDefault();
    if (otp.trim().length === 6) {
      setOtpLoading(true);
      setError("");

      const result = await authAPI.loginWithOTP(email.trim(), otp.trim());

      if (result.success) {
        const userData = {
          id: result.data.customer?.companyCustomerId || result.data.customerId || Date.now(),
          name: result.data.customer?.companyCustomerName || result.data.companyCustomerName || "User",
          email: email.trim(),
          phone: result.data.customer?.companyCustomerPriPhone || result.data.companyCustomerPriPhone || "",
          avatar:
            (result.data.customer?.companyCustomerName || result.data.companyCustomerName || "U").charAt(0).toUpperCase(),
          isPro: false,
          ...result.data,
          ...(result.data.customer || {}), // Flatten customer data to top level
          token: result.data.token // Ensure top-level token is preserved
        };

        // Set external_id for chat persistence
        if (result.data.customerId) {
          localStorage.setItem("external_id", result.data.customerId.toString());
        }

        // Decode token and persist chat session details (OTP)
        try {
          if (result.data.token) {
            const decoded = jwtDecode(result.data.token);
            const userRole = decoded.role;
            const userId = decoded.userId;
            const userToken = decoded.userToken || result.data.token;

            // Update user session in localStorage
            const chatSession = {
              userId: userId,
              userToken: userToken,
              conversationId: "",
              webchatId: userId,
              lastMessageId: null
            };

            localStorage.setItem(WEBCHAT_CONFIG.clientName, JSON.stringify(chatSession));
            console.log("✅ Chat session seeded from OTP Login:", chatSession);

            if (userId && userRole && WEBCHAT_CONFIG.uuid) {
              console.log("Assigning role:", userRole, "to userId:", userId);
              authAPI.addUserRole(WEBCHAT_CONFIG.uuid, userId, userRole, CHAT_AUTH_TOKEN)
                .catch(err => console.error("Error assigning user role:", err));
            }
          }
        } catch (err) {
          console.error("Error processing OTP login token:", err);
        }

        onLogin(userData);
      } else {
        setError(result.error);
      }

      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError("");
    setOtpLoading(true);

    const result = await authAPI.sendLoginOTP(email.trim());

    if (!result.success) {
      setError(result.error);
    } else {
      setError("OTP resent successfully!");
    }

    setOtpLoading(false);
  };

  return (
    <Layout
      product={product}
      title="Log in to your account"
      subtitle="Welcome back. Pick up where you left off."
      headerLink={{ to: signupPath, label: "Start free trial" }}
      cardFooter={
        <>
          Don't have an account?{" "}
          <Link to={signupPath} className={authLinkClass}>
            Start your free trial
          </Link>
        </>
      }
    >
      <AuthAlert message={showOtpModal ? "" : error} />

      <form onSubmit={handlePasswordLogin} className="grid gap-4">
        <div>
          <label htmlFor="login-email" className={authLabelClass}>
            Work email
          </label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            className={authInputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="login-password" className={authLabelClass}>
            Password
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              className={`${authInputClass} pr-12`}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={authEyeButtonClass}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
        </div>

        {/* Remember Me & Login with OTP & Forgot Password */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded accent-cyan-500"
            />
            <span className="text-[13px] text-gray-600 dark:text-gray-400">Remember me</span>
          </label>
          <div className="flex gap-4 text-[13px]">
            <Link to="/forgot-password" className={authLinkClass}>
              Forgot password?
            </Link>
            <button type="button" onClick={handleSendOTP} className={authLinkClass}>
              Log in with OTP
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={!email.trim() || !password.trim() || isLoading}
          className={authButtonClass}
        >
          {isLoading ? "Logging in..." : "Log In"}
        </button>
      </form>

      <AuthSecurityNote>
        Secure login with password or one-time code. We never share your email.
      </AuthSecurityNote>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <AuthOtpModal
          title="Enter OTP"
          email={email}
          error={error}
          otp={otp}
          setOtp={setOtp}
          onSubmit={handleOTPLogin}
          loading={otpLoading}
          submitLabel="Verify & Log In"
          loadingLabel="Verifying..."
          onResend={handleResendOTP}
          onCancel={() => {
            setShowOtpModal(false);
            setOtp("");
            setError("");
          }}
        />
      )}
    </Layout>
  );
};

export default Login;
